/**
 * Note Dialog Component
 * Dialog for creating and editing notes attached to entities
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Autocomplete,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  NoteAdd as NoteAddIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  Note,
  NoteCreate,
  NoteUpdate,
  EntityType,
  NotePriority,
  createNote,
  updateNote,
  getChoices,
  getNotesForEntity,
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  NOTE_PRIORITIES,
} from '../../services/notesApi';

interface NoteDialogProps {
  open: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: string;
  entityLabel?: string;
  latitude?: number;
  longitude?: number;
  existingNote?: Note;  // If editing an existing note
  onSave?: (note: Note) => void;
}

const NoteDialog: React.FC<NoteDialogProps> = ({
  open,
  onClose,
  entityType,
  entityId,
  entityLabel,
  latitude,
  longitude,
  existingNote,
  onSave,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState<NotePriority>('normal');
  const [tags, setTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [existingNotes, setExistingNotes] = useState<Note[]>([]);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const isEditing = !!existingNote;
  
  // Load choices and existing notes on open
  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, entityType, entityId]);
  
  // Populate form if editing
  useEffect(() => {
    if (existingNote) {
      setTitle(existingNote.title || '');
      setContent(existingNote.content);
      setPriority(existingNote.priority);
      setTags(existingNote.tags || []);
    } else {
      // Reset form for new note
      setTitle('');
      setContent('');
      setPriority('normal');
      setTags([]);
    }
  }, [existingNote, open]);
  
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load available tags
      const choices = await getChoices();
      setAvailableTags(choices.existing_tags);
      
      // Load existing notes for this entity
      const notes = await getNotesForEntity(entityType, entityId);
      setExistingNotes(notes);
    } catch (err) {
      console.error('Error loading note data:', err);
      // Don't show error for loading - just continue with empty data
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    if (!content.trim()) {
      setError('Note content is required');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      let savedNote: Note;
      
      if (isEditing && existingNote) {
        // Update existing note
        const updateData: NoteUpdate = {
          title: title || undefined,
          content,
          priority,
          tags,
        };
        savedNote = await updateNote(existingNote.id, updateData);
      } else {
        // Create new note
        const createData: NoteCreate = {
          entity_type: entityType,
          entity_id: entityId,
          entity_label: entityLabel,
          title: title || undefined,
          content,
          priority,
          tags,
          latitude,
          longitude,
        };
        savedNote = await createNote(createData);
      }
      
      if (onSave) {
        onSave(savedNote);
      }
      
      onClose();
    } catch (err: any) {
      console.error('Error saving note:', err);
      setError(err.response?.data?.detail || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };
  
  const getEntityTypeLabel = (type: EntityType): string => {
    const labels: Record<EntityType, string> = {
      device: 'Device',
      location: 'Location',
      entity: 'Entity',
      link: 'Connection',
      geofence: 'Geofence',
    };
    return labels[type] || type;
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDarkMode ? '#1a1a2e' : '#ffffff',
          backgroundImage: 'none',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NoteAddIcon sx={{ color: isDarkMode ? '#f59e0b' : '#ed6c02' }} />
          <Typography variant="h6">
            {isEditing ? 'Edit Note' : 'Add Note'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Entity Info */}
            <Box sx={{ 
              mb: 3, 
              p: 2, 
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              borderRadius: 1,
            }}>
              <Typography variant="body2" color="text.secondary">
                {getEntityTypeLabel(entityType)}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {entityLabel || entityId}
              </Typography>
              {latitude && longitude && (
                <Typography variant="caption" color="text.secondary">
                  {latitude.toFixed(6)}, {longitude.toFixed(6)}
                </Typography>
              )}
            </Box>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {/* Existing Notes Summary */}
            {existingNotes.length > 0 && !isEditing && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This {getEntityTypeLabel(entityType).toLowerCase()} has {existingNotes.length} existing note{existingNotes.length > 1 ? 's' : ''}.
              </Alert>
            )}
            
            {/* Title */}
            <TextField
              label="Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              placeholder="Brief title for your note"
            />
            
            {/* Content */}
            <TextField
              label="Note Content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              fullWidth
              multiline
              rows={4}
              required
              sx={{ mb: 2 }}
              placeholder="Enter your observations, findings, or comments..."
            />
            
            {/* Priority */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priority}
                label="Priority"
                onChange={(e) => setPriority(e.target.value as NotePriority)}
              >
                {NOTE_PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%', 
                          bgcolor: PRIORITY_COLORS[p] 
                        }} 
                      />
                      {PRIORITY_LABELS[p]}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Tags */}
            <Autocomplete
              multiple
              freeSolo
              options={availableTags}
              value={tags}
              onChange={(_, newValue) => setTags(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    sx={{ 
                      bgcolor: isDarkMode ? 'rgba(96, 165, 250, 0.2)' : 'rgba(33, 150, 243, 0.1)',
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Add tags..."
                  helperText="Type and press Enter to add custom tags"
                />
              )}
            />
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        pb: 2,
        borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || !content.trim()}
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{
            bgcolor: isDarkMode ? '#f59e0b' : '#ed6c02',
            '&:hover': {
              bgcolor: isDarkMode ? '#d97706' : '#c55a02',
            }
          }}
        >
          {saving ? 'Saving...' : (isEditing ? 'Update Note' : 'Save Note')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoteDialog;

